/**
 * Notification Email Watcher Service
 * Watches for new notifications with send_email flag and automatically sends emails
 */

import { supabase } from '../utils/supabase';
import emailNotificationService from '../utils/emailNotificationService';

class NotificationEmailWatcher {
  constructor() {
    this.isWatching = false;
    this.subscription = null;
    this.processingQueue = new Set();
    this.isDevelopment = import.meta.env.MODE === 'development';
  }

  /**
   * Start watching for notifications that need email sending
   */
  startWatching() {
    if (this.isWatching) {
      console.log('[NotificationEmailWatcher] Already watching');
      return;
    }

    if (this.isDevelopment) {
      console.log('[NotificationEmailWatcher] Starting notification email watcher');
    }

    this.isWatching = true;

    // Subscribe to new notifications
    this.subscription = supabase
      .channel('notification_email_watcher')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        async (payload) => {
          await this.handleNewNotification(payload.new);
        }
      )
      .subscribe();
  }

  /**
   * Stop watching
   */
  stopWatching() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.isWatching = false;
    
    if (this.isDevelopment) {
      console.log('[NotificationEmailWatcher] Stopped watching');
    }
  }

  /**
   * Handle a new notification
   * @param {Object} notification - The notification object
   */
  async handleNewNotification(notification) {
    try {
      // Check if this notification needs an email
      const sendEmail = notification.data?.send_email === true || notification.data?.send_email === 'true';
      
      if (!sendEmail) {
        return;
      }

      // Prevent duplicate processing
      if (this.processingQueue.has(notification.id)) {
        return;
      }

      this.processingQueue.add(notification.id);

      if (this.isDevelopment) {
        console.log('[NotificationEmailWatcher] Processing notification for email:', notification.id);
      }

      // Get email data from notification
      const emailType = notification.data?.email_type;
      const emailData = notification.data?.email_data;

      if (!emailType || !emailData) {
        console.warn('[NotificationEmailWatcher] Missing email_type or email_data:', notification.id);
        this.processingQueue.delete(notification.id);
        return;
      }

      // Check if already sent
      if (notification.data?.email_sent_at) {
        if (this.isDevelopment) {
          console.log('[NotificationEmailWatcher] Email already sent for notification:', notification.id);
        }
        this.processingQueue.delete(notification.id);
        return;
      }

      // Send the email
      const result = await emailNotificationService.sendEmail(
        emailData.email,
        emailData.fullName,
        emailType,
        emailData
      );

      if (result.success) {
        // Update notification to mark email as sent
        await supabase
          .from('notifications')
          .update({
            data: {
              ...notification.data,
              email_sent_at: new Date().toISOString(),
              email_message_id: result.messageId
            }
          })
          .eq('id', notification.id);

        if (this.isDevelopment) {
          console.log('[NotificationEmailWatcher] Email sent successfully:', notification.id);
        }
      } else {
        console.error('[NotificationEmailWatcher] Failed to send email:', result.error);
        
        // Mark as failed
        await supabase
          .from('notifications')
          .update({
            data: {
              ...notification.data,
              email_failed_at: new Date().toISOString(),
              email_error: result.error
            }
          })
          .eq('id', notification.id);
      }

      // Remove from processing queue
      this.processingQueue.delete(notification.id);

    } catch (error) {
      console.error('[NotificationEmailWatcher] Error processing notification:', error);
      this.processingQueue.delete(notification.id);
    }
  }

  /**
   * Process any existing notifications that haven't been sent yet
   * Call this on initial load to catch any missed notifications
   */
  async processBacklog() {
    try {
      if (this.isDevelopment) {
        console.log('[NotificationEmailWatcher] Processing backlog...');
      }

      // Get notifications that need email but haven't been sent
      const { data: pendingNotifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('data->>send_email', 'true')
        .is('data->>email_sent_at', null)
        .is('data->>email_failed_at', null)
        .order('created_at', { ascending: true })
        .limit(50); // Process in batches

      if (error) {
        console.error('[NotificationEmailWatcher] Error fetching backlog:', error);
        return;
      }

      if (pendingNotifications && pendingNotifications.length > 0) {
        if (this.isDevelopment) {
          console.log(`[NotificationEmailWatcher] Found ${pendingNotifications.length} pending email notifications`);
        }

        // Process each notification
        for (const notification of pendingNotifications) {
          await this.handleNewNotification(notification);
          
          // Small delay between emails to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

    } catch (error) {
      console.error('[NotificationEmailWatcher] Error processing backlog:', error);
    }
  }
}

// Create singleton instance
const notificationEmailWatcher = new NotificationEmailWatcher();

export default notificationEmailWatcher;
